import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Link2, ClipboardPaste, Wand2, Sparkles } from "lucide-react";

/**
 * "Already listed somewhere else?" — customer link-import card.
 * Sits at the top of the post-ad form and prefills fields from a pasted link
 * (Facebook Marketplace, Trade Me, …) or a pasted ad text.
 *
 * ⚠️ Hard rule: NO "AI"/"GPT"/"smart-fill" copy anywhere in this flow. Copy is
 * intentionally phrased as "we'll try to get your details".
 */
export default function ImportFromLinkCard({ onExtracted, disabled }) {
  const [mode, setMode] = useState("link"); // "link" | "text"
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");

  const extractMut = useMutation({
    mutationFn: async () => {
      const body = mode === "link" ? { url: url.trim() } : { text: text.trim() };
      // Backend contract allows up to 30s — override the 20s global axios timeout.
      const { data } = await sharedApi.post("/classifieds/listing-import/extract", body, { timeout: 45000 });
      return data;
    },
    onSuccess: async (data) => {
      // Re-host any remote images via our own /uploads/image so we own the URLs.
      const remoteImages = data.images || [];
      const rehosted = [];
      for (const src of remoteImages) {
        try {
          const { data: up } = await sharedApi.post("/uploads/image", { data: src });
          if (up?.url) rehosted.push(up.url);
        } catch { /* skip bad image */ }
      }
      onExtracted?.({ listing: data.listing || {}, images: rehosted, source: data.source });
      toast.success("Details filled in — review and edit before publishing.");
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || "We couldn't get those details. Try pasting the ad text instead.";
      toast.error(String(detail));
    },
  });

  const canSubmit = mode === "link" ? url.trim().length > 8 : text.trim().length > 30;
  const isBusy = extractMut.isPending || disabled;

  return (
    <div
      className="rounded-3xl bg-gradient-to-br from-[#EEF3FB] via-white to-[#F5F1EA] p-5 md:p-6 border border-soft"
      data-testid="import-from-link-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl bg-white shadow-sm grid place-items-center text-[color:var(--brand-blue)] shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-slate-900 text-lg">Already listed somewhere else?</h2>
          <p className="mt-1 text-sm text-slate-600">
            Paste the link (Facebook Marketplace, Trade Me…) and we&apos;ll try to get your details.
          </p>

          {/* Mode toggle */}
          <div className="mt-4 inline-flex bg-white rounded-full p-1 border border-soft" data-testid="import-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("link")}
              data-testid="import-mode-link"
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${
                mode === "link" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Link2 className="w-3.5 h-3.5" /> Paste link
            </button>
            <button
              type="button"
              onClick={() => setMode("text")}
              data-testid="import-mode-text"
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${
                mode === "text" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <ClipboardPaste className="w-3.5 h-3.5" /> Paste ad text instead
            </button>
          </div>

          <div className="mt-3">
            {mode === "link" ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  data-testid="import-url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  type="url"
                  placeholder="https://www.facebook.com/marketplace/item/… or Trade Me URL"
                  className="flex-1 px-4 py-2.5 rounded-full bg-white border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                />
                <button
                  type="button"
                  onClick={() => extractMut.mutate()}
                  disabled={!canSubmit || isBusy}
                  data-testid="import-submit-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 font-bold text-sm bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white disabled:opacity-60 transition-colors"
                >
                  {extractMut.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Getting your details…</>
                  ) : (
                    <><Wand2 className="w-4 h-4" /> Fetch my details</>
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <textarea
                  data-testid="import-text-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={5}
                  placeholder={"Paste your ad text here — title, description, price, location, condition, etc."}
                  className="w-full px-4 py-2.5 rounded-2xl bg-white border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm resize-y"
                />
                <button
                  type="button"
                  onClick={() => extractMut.mutate()}
                  disabled={!canSubmit || isBusy}
                  data-testid="import-submit-btn"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 font-bold text-sm bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white disabled:opacity-60 transition-colors"
                >
                  {extractMut.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Getting your details…</>
                  ) : (
                    <><Wand2 className="w-4 h-4" /> Fill in from my text</>
                  )}
                </button>
              </div>
            )}
          </div>

          <p className="mt-2 text-[11px] text-slate-500">
            This may take up to 30 seconds. If a page is private (like some Facebook items), paste the ad text instead — we&apos;ll fill in what we can.
          </p>
        </div>
      </div>
    </div>
  );
}
